import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
    deleteSelectedUser,
    getAsyncSpecificationId,
    getEntitlementsForSelectedUser,
    getMaxAllowedSitesWithoutAddonForSelectedHost,
    getSubscriptionsForSelectedUser,
    getTotalOwnedSitesForSelectedHost,
    getUserDetails,
    resetAgentUserDetail
} from "../../store/Agent/agentUserDetailSlice";
import { User } from '../../services/restserver-openapi';
import { Main } from "../../layouts";
import Container from "../../components/Container";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Link,
    Snackbar,
    Typography,
    IconButton, Paper
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    ArrowBackIosNewRounded,
    BadgeRounded,
    Delete as DeleteIcon,
    ReceiptLongRounded,
    RouteRounded,
    SettingsRounded
} from "@mui/icons-material";
import HostDetails from "../../components/agentComponents/HostDetails";
import VanliferDetails from "../../components/agentComponents/VanliferDetails";
import TravelAgentDetails from "../../components/agentComponents/TravelAgentDetails";
import AboOverview from "../../components/profileComponents/AboOverview";

const AgentUserDetailPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const mode = theme.palette.mode;
    const { userRole, userId } = useParams<{ userRole: string, userId: string }>();
    const selectedUser = useAppSelector(state => state.agentUserDetail.selectedUser);
    const specificationId = useAppSelector(state => state.agentUserDetail.specificationId);
    const subscriptions = useAppSelector(state => state.agentUserDetail.subscriptions);
    const totalOwnedSites = useAppSelector(state => state.agentUserDetail.selectedHost.totalOwnedSites);
    const maxAllowedSitesWithoutAddon = useAppSelector(state => state.agentUserDetail.selectedHost.maxAllowedSitesWithoutAddon);
    const userEntitlements = useAppSelector(state => state.agentUserDetail.userEntitlements);
    const isVerifiedHost = useAppSelector(state => state.agentUserDetail.selectedHost.isVerified);
    const isVerifiedAgent = useAppSelector(state => state.agentUserDetail.selectedAgent.isVerified);
    const loggedInUserIsVerifiedAgent = useAppSelector(state => state.user.isVerifiedAgent);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");
    const [activeSection, setActiveSection] = useState<'profile' | 'subscription' | 'travel' | 'actions'>('profile');

    const sectionPaperSx = {
        borderRadius: 4,
        p: 2,
        border: 0,
        boxShadow: "none",
        bgcolor: "background.paper",
    } as const;

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        onClick?: () => void;
    }) => {
        const { icon, title, subtitle, active, onClick } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onClick={onClick}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        "&.Mui-selected": {
                            bgcolor: "action.selected",
                            "&:hover": { bgcolor: "action.selected" },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={<Typography sx={{ fontWeight: 800 }}>{title}</Typography>}
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: "text.secondary" }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    const scrollToSection = (sectionId: 'profile' | 'subscription' | 'travel' | 'actions') => {
        setActiveSection(sectionId);
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const UserSpecificComponent = () => {
        switch (userRole) {
            case User.userRole.HOST:
                return <>
                        <Typography variant={'caption'} color={'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase' }}>Compounds</Typography>
                        <HostDetails user={selectedUser} hostId={specificationId} totalSites={totalOwnedSites} isVerified={isVerifiedHost} />
                    </>;
            case User.userRole.VANLIFER:
                return <>
                        <VanliferDetails user={selectedUser} vanliferId={specificationId} />
                    </>;
            case User.userRole.AGENT:
                return <TravelAgentDetails user={selectedUser} agentId={specificationId} isVerified={isVerifiedAgent} />;
            default:
                return <div>Invalid user role</div>;
        }
    }

    useEffect(() => {
        if (userRole && userId) {
            dispatch(getAsyncSpecificationId({ userId: parseInt(userId), userRoleInput: userRole as User.userRole }))
                .unwrap()
                .then(payload => {
                    dispatch(getUserDetails({ userSpecificationId: payload, userRoleInput: userRole }));
                    dispatch(getSubscriptionsForSelectedUser({ userId: parseInt(userId) }));
                    dispatch(getEntitlementsForSelectedUser({ userId: parseInt(userId) }));
                    if (userRole === User.userRole.HOST) {
                        dispatch(getTotalOwnedSitesForSelectedHost({ hostId: parseInt(userId) }));
                        dispatch(getMaxAllowedSitesWithoutAddonForSelectedHost({ hostId: parseInt(userId) }));
                    }
                });
        }
    }, [dispatch, userId, userRole]);

    const goBack = () => {
        dispatch(resetAgentUserDetail());
        navigate(-1);
    };

    const handleDelete = async () => {
        if (userRole && specificationId) {
            try {
                const resultAction = await dispatch(deleteSelectedUser({
                    specificationId,
                    userRole: userRole as User.userRole
                }));

                if (deleteSelectedUser.fulfilled.match(resultAction)) {
                    goBack(); // alles gut
                } else {
                    // Optional: detaillierter Fehlertext, falls vorhanden
                    const errorMessage =
                        resultAction?.payload?.toString() ||
                        resultAction?.error?.message ||
                        "Fehler beim Löschen des Users";

                    setErrorSnackbarMessage(errorMessage);
                    setOpenErrorSnackbar(true);
                }
            } catch (error) {
                // Catch für unerwartete Fehler
                setErrorSnackbarMessage("Ein unerwarteter Fehler ist aufgetreten.");
                setOpenErrorSnackbar(true);
            } finally {
                setOpenDeleteDialog(false);
            }
        }
    };


// Alte Version ohne Error-Handling
    // const handleDelete = async () => {
    //     if (userRole && specificationId) {
    //         dispatch(deleteSelectedUser({  specificationId: specificationId, userRole: userRole as User.userRole }))
    //             .then(resultAction => {
    //                 if (resultAction.meta.requestStatus === 'fulfilled') {
    //                     goBack();
    //                 } else {
    //                     setErrorSnackbarMessage("Fehler beim Löschen des Users");
    //                     setOpenErrorSnackbar(true);
    //                 }
    //                 setOpenDeleteDialog(false);
    //             });
    //     }
    // };

    if (!loggedInUserIsVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Alert severity="error">Sie haben keine Agent-Berechtigung.</Alert>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Snackbar open={openErrorSnackbar} autoHideDuration={6000} onClose={() => setOpenErrorSnackbar(false)}>
                <Alert onClose={() => setOpenErrorSnackbar(false)} severity="error">{errorSnackbarMessage}</Alert>
            </Snackbar>
            <Container>
                <Typography
                    sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: 1,
                    }}
                    gutterBottom
                    color="text.secondary"
                >
                    Administration
                </Typography>

                <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <IconButton onClick={goBack} size="large">
                            <ArrowBackIosNewRounded />
                        </IconButton>
                        <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                            {selectedUser.person?.lastname}, {selectedUser.person?.firstname}
                            <Typography
                                component="span"
                                variant="inherit"
                                color="text.primary"
                                sx={{
                                    ml: 1,
                                    background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                        mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main,
                                        0.3
                                    )} 0%)`,
                                }}
                            >
                                ({userRole})
                            </Typography>
                        </Typography>
                    </Box>

                    <Typography variant="h6" color="text.secondary">
                        {selectedUser.person?.lastname}, {selectedUser.person?.firstname} ({userRole})
                    </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
                    <Paper elevation={0} sx={{ ...sectionPaperSx, height: 'fit-content' }}>
                        <List sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            <ActionRow
                                icon={<BadgeRounded />}
                                title="Userdaten & Kontakt"
                                subtitle="Basis- und Profildaten"
                                active={activeSection === 'profile'}
                                onClick={() => scrollToSection('profile')}
                            />
                            <ActionRow
                                icon={<ReceiptLongRounded />}
                                title="Subscription"
                                subtitle="Abo & Entitlements"
                                active={activeSection === 'subscription'}
                                onClick={() => scrollToSection('subscription')}
                            />
                            <ActionRow
                                icon={<RouteRounded />}
                                title="Reiseübersicht"
                                subtitle="Rollenabhängige Details"
                                active={activeSection === 'travel'}
                                onClick={() => scrollToSection('travel')}
                            />
                            <ActionRow
                                icon={<SettingsRounded />}
                                title="Aktionen"
                                subtitle="Benutzer verwalten"
                                active={activeSection === 'actions'}
                                onClick={() => scrollToSection('actions')}
                            />
                        </List>
                    </Paper>

                    <Box sx={{ display: "grid", gap: 2 }}>
                    <Paper id="profile" elevation={0} sx={sectionPaperSx}>
                        <Typography variant={'h6'} color={'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5 }}>
                            Userdaten & Kontakt
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 3 }} elevation={0}>
                                    <Typography variant="body1">User ID: {selectedUser.userId}</Typography>
                                    <Typography variant="body1">CommunityName: {selectedUser.communityName || '-'}</Typography>
                                    <Typography variant="body1">
                                        Chargebee ID: {selectedUser.chargebeeId ? (
                                        <Link
                                            href={`https://${process.env.REACT_APP_CHARGEBEE_SITE}.chargebee.com/d/customers/${selectedUser.chargebeeId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {selectedUser.chargebeeId}
                                        </Link>
                                    ) : '-'}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 3 }} elevation={0}>
                                    <Typography variant="body1">{selectedUser.person?.firstname} {selectedUser.person?.lastname} ({selectedUser.person?.gender})</Typography>
                                    <Typography variant="body1">{selectedUser.person?.address?.line1 || '-'}</Typography>
                                    <Typography variant="body1">{selectedUser.person?.address?.zipCode} {selectedUser.person?.address?.city}, {selectedUser.person?.address?.country}</Typography>
                                    <Typography variant="body1">{selectedUser.person?.phone || '-'}</Typography>
                                    <Typography variant="body1">{selectedUser.person?.email || '-'}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper id="subscription" elevation={0} sx={sectionPaperSx}>
                        <Typography variant={'h6'} color={'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5 }}>
                            Subscription & Entitlements
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <AboOverview
                                    subscriptions={subscriptions}
                                    maxAllowedSitesWithoutAddon={maxAllowedSitesWithoutAddon}
                                    totalOwnedSites={totalOwnedSites}
                                    userEntitlements={userEntitlements}
                                    userRole={userRole as User.userRole}
                                    editable={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 3, height: '100%' }} elevation={0}>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {userEntitlements.map(entitlement => (
                                            <li key={entitlement.featureId}>
                                                <Typography variant={'body2'}>{entitlement.featureName} (Value: {entitlement.value})</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper id="travel" elevation={0} sx={sectionPaperSx}>
                        <Typography variant={'h6'} color={'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5 }}>
                            Reiseübersicht
                        </Typography>
                        <UserSpecificComponent />
                    </Paper>

                    <Paper id="actions" elevation={0} sx={sectionPaperSx}>
                        <Typography variant={'h6'} color={'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5 }}>
                            Aktionen
                        </Typography>
                        <Box display="flex" justifyContent="flex-start">
                            <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setOpenDeleteDialog(true)}>
                                Löschen
                            </Button>
                        </Box>
                    </Paper>
                    </Box>
                </Box>

                <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                    <DialogTitle>User löschen?</DialogTitle>
                    <DialogActions>
                        <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
                        <Button onClick={handleDelete} color="error">Löschen</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Main>
    );
};

export default AgentUserDetailPage;
